export interface Message {
  type: string,
}
export interface FullMessage extends Message {
  brokerId: string,
  id: string,
}

interface MessageAck extends FullMessage {
  type: 'ack',
  response?: Message,
}

type MessageBrokerArgs = { send: Window, receive: Window };

type TypedMessageListener<T extends Message> = (arg: T) => Promise<Message | undefined | void> | Message | undefined | void;
type MessageListener = TypedMessageListener<Message>;

// Wrapper around postMessage events that lets us use promises instead of
// disjoint "send" and "receive" flows.
export class MessageBroker {
  // Keep track of which window to send to and which to receive on.
  // These are promises so that we can effectively buffer messages until the window is ready.
  _sendWindow: Promise<Window>;
  _receiveWindow: Promise<Window>;
  _setSendWindow?: (value: Window) => void;
  _setReceiveWindow?: (value: Window) => void;

  // Raw "message" event listener. We need to keep track of this so we can remove it later.
  messageHandler: (event: MessageEvent) => void;

  // Map of in-progress promises. When we receive an ack message with a matching id, we resolve the promise.
  promises: Map<string, {
    promise: Promise<Message | undefined>,
    resolve: (value?: Message) => void,
  }>;

  // Map of listeners. When we receive a message with a matching type, we call the listener.
  listeners: Map<string, MessageListener>;

  // Unique id for this broker. Used to distinguish messages from other brokers.
  id: string;

  origin: string;

  constructor(id?: string) {
    this._sendWindow = new Promise(resolve => this._setSendWindow = resolve);
    this._receiveWindow = new Promise(resolve => this._setReceiveWindow = resolve);

    this.messageHandler = event => {
      if (this.origin !== '*' && event.origin !== this.origin) return;
      this.handleMessage(event);
    };

    this.id = id ?? crypto.randomUUID();
    this.origin = '*';
    this.promises = new Map();
    this.listeners = new Map();
  }

  // Call this to set up the broker
  setup(arg: MessageBrokerArgs) {
    if (this._setSendWindow) {
      this._setSendWindow(arg.send);
      this._setSendWindow = undefined;
    } else {
      this._sendWindow = Promise.resolve(arg.send);
    }

    if (this._setReceiveWindow) {
      this._setReceiveWindow(arg.receive);
      this._setReceiveWindow = undefined;
    } else {
      this._receiveWindow = Promise.resolve(arg.receive);
    }

    arg.receive.addEventListener('message', this.messageHandler);
  }

  // Call this to send a message. Promise will resolve when the associated ack message
  // comes back.
  send<MessageType extends Message>(message: MessageType): Promise<Message | undefined> {
    const fullMessage: FullMessage = {
      ...message,
      brokerId: this.id,
      id: crypto.randomUUID(),
    };

    let resolve: ((value?: Message) => void) | null = null;
    const promise = new Promise<Message | undefined>((resolvePromise, _reject) => {
      resolve = resolvePromise;
    });
    if (!resolve) throw new Error('Broker is busted');

    this.promises.set(fullMessage.id, { promise, resolve });
    return this._sendWindow.then(w => w.postMessage(fullMessage, this.origin)).then(() => promise);
  }

  // Call this to set the listener for a certain event. Kind of like the built-in
  // addEventListener, but can only add one per event type.
  receive<MessageType extends Message>(type: MessageType['type'], listener: TypedMessageListener<MessageType>) {
    this.listeners.set(type, listener as MessageListener);
  }

  // Internal message handler
  async handleMessage(event: MessageEvent<FullMessage>) {
    const message = event.data;

    // Ignore messages from other brokers
    if (message.brokerId !== this.id) return;

    // 'ack' messages resolve an in-progress promise
    if (message.type === 'ack') {
      const ack = message as MessageAck;
      const promise = this.promises.get(ack.id);

      // Acks with a wrong ID typically come from another <komoju-fields> tag
      // in the same window.
      if (!promise) return;

      promise.resolve(ack.response);
      this.promises.delete(ack.id);
    }
    // all other messages trigger a local listener if present
    else {
      const listener = this.listeners.get(message.type);
      const ack: MessageAck = { type: 'ack', brokerId: this.id, id: message.id };
      if (listener) ack.response = (await listener(message)) ?? undefined;
      await this._sendWindow.then(w => w.postMessage(ack, this.origin));
    }
  }

  // Call this to clean up the window event listener
  destroy() {
    return this._receiveWindow.then(w => w.removeEventListener('message', this.messageHandler));
  }
}
